import React, { useState, useEffect, useRef } from 'react';
import { 
  Sparkles, Menu, X, Send, Plus, Loader2, MessageSquare, ShoppingBag, Heart
} from 'lucide-react';
import { Product, CartItem, ChatMessage, ChatSession } from '../../types';
import { db } from '../../services/firebase';
import { collection, query, getDocs, limit, where, or, and } from 'firebase/firestore';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { useAuth } from '../../contexts/AuthContext';

interface ScoopAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  contextProduct?: Product | null;
  onSelectProduct: (product: Product) => void;
  cart: CartItem[];
  wishlist: any[];
}

interface ExtendedChatMessage extends ChatMessage {
  products?: Product[];
  suggestedTags?: string[];
}

interface ExtendedChatSession extends Omit<ChatSession, 'messages'> {
  messages: ExtendedChatMessage[];
}

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || "";
const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);

const getDefaultSession = (): ExtendedChatSession => ({
  id: '1',
  title: 'Scoop AI',
  date: new Date().toLocaleDateString(),
  messages: [
    {
      id: '1',
      senderId: 'ai',
      text: "✨ Hey gorgeous! I'm Scoop AI, your personal digital stylist! 🎨\n\nI can help you with:\n• Analyzing your cart to suggest matching pieces\n• Building complete outfits from any item\n• Finding products based on your style & budget\n• Discovering new looks from your wishlist\n\nWhat are we shopping for today? 👀",
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ],
  preview: "Ready to style!",
  isAI: true
});

export const ScoopAIModal: React.FC<ScoopAIModalProps> = ({
  isOpen,
  onClose,
  contextProduct,
  onSelectProduct,
  cart,
  wishlist
}) => {
  const { user } = useAuth();
  
  const storageKey = user?.uid ? `scoop_ai_memory_${user.uid}` : 'scoop_ai_memory_guest';

  const [sessions, setSessions] = useState<ExtendedChatSession[]>([getDefaultSession()]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('1');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const currentMessages = sessions.find(s => s.id === currentSessionId)?.messages || [];

  // Load saved sessions
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSessions(parsed);
        setCurrentSessionId(parsed.length > 0 ? parsed[0].id : '1');
      } catch (e) {
        console.error("Failed to parse saved chats", e);
      }
    }
  }, [storageKey]);

  // Save sessions
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(sessions));
  }, [sessions, storageKey]);

  // Auto-scroll to bottom
  useEffect(() => { 
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); 
  }, [currentMessages, isLoading]);

  // Enhanced product search with multiple filters
  // Enhanced product search that actually works on localhost
const searchProducts = async (tags: string[], style?: string, maxPrice?: number): Promise<Product[]> => {
  try {
    const productsRef = collection(db, 'products');
    
    // Simple query first - get a reasonable sample
    const q = query(productsRef, limit(30));
    const snap = await getDocs(q);
    
    if (snap.empty) {
      console.warn("No products found in Firestore");
      return [];
    }
    
    let products = snap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as Product));
    
    // Client-side filtering (RELIABLE for localhost)
    if (tags && tags.length > 0) {
      products = products.filter(product => 
        tags.some(tag => 
          product.name?.toLowerCase().includes(tag.toLowerCase()) ||
          product.category?.toLowerCase().includes(tag.toLowerCase()) ||
          product.brand?.toLowerCase().includes(tag.toLowerCase()) ||
          (product.tags && product.tags.some((t: string) => t.toLowerCase().includes(tag.toLowerCase())))
        )
      );
    }
    
    // Price filter
    if (maxPrice) {
      products = products.filter(p => p.price <= maxPrice);
    }
    
    // Style filter
    if (style && style !== 'null') {
      const styleLower = style.toLowerCase();
      products = products.filter(p => 
        p.category?.toLowerCase().includes(styleLower) ||
        p.name?.toLowerCase().includes(styleLower) ||
        (p.tags && p.tags.some((t: string) => t.toLowerCase().includes(styleLower)))
      );
    }
    
    return products.slice(0, 8);
    
  } catch (error) {
    console.error("Product search error:", error);
    // Return empty array - Gemini will still work
    return [];
  }
};

  // Analyze cart for better recommendations
  const analyzeCartContext = (): string => {
    if (cart.length === 0) return "No items in cart";
    
    const cartItems = cart.map(item => item.name);
    const categories = cart.map(item => item.category).filter(Boolean);
    const avgPrice = cart.reduce((sum, item) => sum + item.price, 0) / cart.length;
    const mainCategory = categories.length > 0 ? 
      categories.sort((a,b) => 
        categories.filter(v => v === a).length - categories.filter(v => v === b).length
      ).pop() : "general";
    
    return `Cart has ${cart.length} items: ${cartItems.join(', ')}. Main category: ${mainCategory}. Average price: R${avgPrice.toFixed(0)}`;
  };

  // Analyze wishlist for preferences
  const analyzeWishlistContext = (): string => {
    if (wishlist.length === 0) return "No items in wishlist";
    
    const wishlistItems = wishlist.slice(0, 5).map(item => item.name);
    const brands = [...new Set(wishlist.map(item => item.brand).filter(Boolean))];
    
    return `Wishlist includes: ${wishlistItems.join(', ')}. Favorite brands: ${brands.slice(0, 3).join(', ')}`;
  };

  const createNewSession = () => {
    const newId = Date.now().toString();
    
    let greeting = "✨ Hey bestie! I'm Scoop AI, ready to help you find the perfect pieces! 🎨\n\nWhat vibe are we going for today? Streetwear? Minimalist? Y2K? Let me know your style and budget! 💫";
    let title = "New Style Session";
    
    if (contextProduct) { 
      greeting = `🔥 OMG that ${contextProduct.name} from ${contextProduct.brand} is ABSOLUTELY FIRE! 🔥\n\nLet me help you build the PERFECT outfit around it. What's your budget for accessories and matching pieces? I'll find you the best matches! 🛍️`; 
      title = `Styling: ${contextProduct.name}`; 
    } else if (cart.length > 0) { 
      greeting = `🛍️ I see you've got ${cart.length} item${cart.length > 1 ? 's' : ''} in your bag! Let me help you complete this look.\n\nBased on what you've chosen, I can suggest matching pieces, accessories, or alternative options. Want me to analyze your cart? 👀`; 
      title = "Cart Styling Session"; 
    } else if (wishlist.length > 0) {
      greeting = `💖 I noticed you've got ${wishlist.length} item${wishlist.length > 1 ? 's' : ''} saved to your wishlist! Great taste! ✨\n\nWant me to find similar items or suggest complete outfits based on your saved pieces?`; 
      title = "Wishlist Inspiration";
    }
    
    const initialMsg: ExtendedChatMessage = { 
      id: Date.now().toString(), 
      senderId: 'ai', 
      text: greeting, 
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
    };
    
    const newSession: ExtendedChatSession = { 
      id: newId, 
      title, 
      date: new Date().toLocaleDateString(), 
      messages: [initialMsg], 
      preview: "New session", 
      isAI: true 
    };
    
    setSessions(prev => [newSession, ...prev]);
    setCurrentSessionId(newId);
    setIsMenuOpen(false);
  };

  const handleSend = async (textOverride?: string) => {
    const textToSend = textOverride || input;
    if (!textToSend.trim() || !currentSessionId) return;
    
    const userMsg: ExtendedChatMessage = { 
      id: Date.now().toString(), 
      senderId: 'me', 
      text: textToSend, 
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
    };
    
    setSessions(prev => prev.map(s => 
      s.id === currentSessionId ? { ...s, messages: [...s.messages, userMsg], preview: textToSend } : s
    ));
    
    setInput('');
    setIsLoading(true);

    try {
      if (!GEMINI_API_KEY) {
        throw new Error("API key is missing.");
      }

      const model = genAI.getGenerativeModel({ 
        model: "gemini-2.0-flash",
        generationConfig: { responseMimeType: "application/json" }
      });

      // Enhanced context analysis
      const cartContext = analyzeCartContext();
      const wishlistContext = analyzeWishlistContext();
      const productContext = contextProduct ? `${contextProduct.name} from ${contextProduct.brand} (${contextProduct.category}) - R${contextProduct.price}` : "None";
      
      const recentMessages = currentMessages.slice(-4).map(m => `${m.senderId === 'me' ? 'User' : 'AI'}: ${m.text}`).join('\n');
      
      const prompt = `
        You are Scoop AI, an extremely friendly, hype, and knowledgeable streetwear stylist based in South Africa.
        
        CONTEXT:
        - User Cart: ${cartContext}
        - User Wishlist: ${wishlistContext}
        - Current Product Being Styled: ${productContext}
        - Recent Conversation: ${recentMessages}
        - Current Query: "${textToSend}"
        
        TASK: Analyze the user's request and provide personalized styling advice with product recommendations.
        
        CONSIDER:
        1. Their budget preferences (ask if not specified)
        2. Their style preferences (streetwear, Y2K, minimalist, oversized, etc.)
        3. Items already in cart (avoid suggesting duplicates)
        4. Wishlist items (suggest similar styles)
        5. The specific product context if available
        
        RULES:
        - Be incredibly enthusiastic and supportive (use emojis, hype them up!)
        - Ask clarifying questions about budget if not mentioned
        - Suggest 3-5 SPECIFIC search tags based on their request
        - Keep responses conversational and warm (South African streetwear tone)
        - MAXIMUM 150 words per response
        
        Return ONLY valid JSON:
        {
          "reply": "your styled response here",
          "tags": ["tag1", "tag2", "tag3", "tag4", "tag5"],
          "style_preference": "detected style or null",
          "suggested_budget": "R0-R5000 or null"
        }
      `;

      const result = await model.generateContent(prompt);
      const responseText = result.response.text();
      const aiData = JSON.parse(responseText);
      
      // Extract budget from response if mentioned
      let maxPrice: number | undefined;
      const budgetMatch = textToSend.match(/R\s*(\d+)/i) || aiData.reply.match(/R\s*(\d+)/i);
      if (budgetMatch) {
        maxPrice = parseInt(budgetMatch[1]);
      }
      
      // Search for products using the suggested tags
      const matchedProducts = await searchProducts(
        aiData.tags || [], 
        aiData.style_preference || undefined,
        maxPrice
      );
      
      // Create context-aware response
      let finalReply = aiData.reply;
      
      // Add cart context to response if relevant
      if (cart.length > 0 && matchedProducts.length > 0) {
        finalReply += `\n\n🛍️ Based on what's in your cart, I found ${matchedProducts.length} piece${matchedProducts.length > 1 ? 's' : ''} that would complement your style perfectly!`;
      }
      
      // Add wishlist context
      if (wishlist.length > 0 && matchedProducts.length === 0) {
        finalReply += `\n\n💖 I noticed some amazing pieces in your wishlist! Want me to suggest similar items or help you style those?`;
      }
      
      const aiMsg: ExtendedChatMessage = { 
        id: (Date.now() + 1).toString(), 
        senderId: 'ai', 
        text: finalReply,
        products: matchedProducts.length > 0 ? matchedProducts : undefined,
        suggestedTags: aiData.tags,
        createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
      };
      
      setSessions(prev => prev.map(s => 
        s.id === currentSessionId ? { ...s, messages: [...s.messages, aiMsg], preview: aiMsg.text } : s
      ));
      
    } catch (error: any) {
      console.error("Gemini Error:", error);
      
      // Intelligent fallback based on context
      let fallbackMessage = "Hmm, I'm having a moment! 😅 Can you tell me more about what style you're looking for and your budget? I want to find you the perfect pieces! ✨";
      
      if (cart.length > 0) {
        fallbackMessage = `I can see you have ${cart.length} item${cart.length > 1 ? 's' : ''} in your cart! 🛍️ Tell me your budget and style preference (streetwear, Y2K, minimalist, etc.), and I'll find pieces that match perfectly! 💫`;
      } else if (wishlist.length > 0) {
        fallbackMessage = `Your wishlist has some amazing taste! 💖 What's your budget for new pieces? I can find similar items or suggest complete outfits! 🎨`;
      }
      
      const errorMsg: ExtendedChatMessage = {
          id: (Date.now() + 1).toString(),
          senderId: 'ai',
          text: fallbackMessage,
          createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setSessions(prev => prev.map(s => 
        s.id === currentSessionId ? { ...s, messages: [...s.messages, errorMsg], preview: "Tell me more!" } : s
      ));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] bg-black flex animate-in fade-in duration-300 overflow-hidden">
      
      {/* MOBILE BACKDROP */}
      {isMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-40 backdrop-blur-sm animate-in fade-in" 
          onClick={() => setIsMenuOpen(false)} 
        />
      )}
      
      {/* LEFT SIDEBAR */}
      <div className={`fixed inset-y-0 left-0 z-50 w-3/4 max-w-xs bg-zinc-950 border-r border-zinc-800 flex flex-col transform transition-transform duration-300 lg:relative lg:translate-x-0 lg:w-[350px] xl:w-[400px] ${
        isMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        
        <div className="p-6 pb-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-xl font-black text-white uppercase italic tracking-tighter flex items-center gap-2">
                <Sparkles size={18} className="text-white" /> Scoop AI
            </h2>
            <button onClick={() => setIsMenuOpen(false)} className="lg:hidden text-zinc-500 hover:text-white">
                <X size={20} />
            </button>
        </div>

        {/* Cart & Wishlist Summary */}
        <div className="px-6 py-4 border-b border-zinc-800 space-y-2">
          {cart.length > 0 && (
            <div className="flex items-center gap-2 text-zinc-400 text-xs">
              <ShoppingBag size={14} />
              <span>{cart.length} item{cart.length > 1 ? 's' : ''} in cart</span>
            </div>
          )}
          {wishlist.length > 0 && (
            <div className="flex items-center gap-2 text-zinc-400 text-xs">
              <Heart size={14} />
              <span>{wishlist.length} saved item{wishlist.length > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        <div className="p-6 pb-2">
          <button 
            onClick={createNewSession} 
            className="w-full py-4 border border-zinc-700 hover:border-white text-white font-black text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-3 lg:rounded-none rounded-xl"
          >
            <Plus size={16} /> New Styling Session
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-hide">
          <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-widest mb-4 px-2">Chat History</p>
          {sessions.map(session => (
            <button 
              key={session.id}
              onClick={() => { setCurrentSessionId(session.id); setIsMenuOpen(false); }}
              className={`w-full text-left p-4 transition-all lg:rounded-none rounded-xl border ${
                currentSessionId === session.id 
                ? 'bg-zinc-900 border-zinc-700' 
                : 'bg-transparent border-transparent hover:bg-zinc-900/50 hover:border-zinc-800'
              }`}
            >
              <p className="text-white font-bold text-sm truncate uppercase tracking-tight">{session.title}</p>
              <p className="text-zinc-500 text-xs truncate mt-1 font-mono">{session.preview}</p>
              <p className="text-zinc-700 text-[10px] mt-3 font-mono">{session.date}</p>
            </button>
          ))}
        </div>
      </div>
      
      {/* RIGHT PANEL: Main Chat Area */}
      <div className="flex-1 flex flex-col h-full relative bg-black min-w-0">
        
        <div className="p-4 lg:p-8 border-b border-zinc-800 flex items-center justify-between bg-black/90 backdrop-blur-md sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsMenuOpen(true)} className="lg:hidden text-zinc-500 hover:text-white transition-colors">
              <Menu size={24} />
            </button>
            <h2 className="text-lg lg:text-3xl font-black text-white uppercase italic tracking-tighter">
              {sessions.find(s => s.id === currentSessionId)?.title || "Style Studio"}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 lg:p-3 bg-zinc-900 hover:bg-white text-zinc-500 hover:text-black rounded-full transition-colors border border-zinc-800">
            <X size={20} className="lg:w-6 lg:h-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 lg:p-10 space-y-6 lg:space-y-8 scrollbar-hide">
          {currentMessages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-zinc-600 opacity-50">
              <MessageSquare size={64} strokeWidth={1} className="mb-6" />
              <p className="font-black uppercase tracking-[0.2em] text-sm text-white mb-2">Start a conversation</p>
              <p className="text-xs text-zinc-600">Ask me for style advice or product recommendations!</p>
            </div>
          ) : (
            currentMessages.map((msg: ExtendedChatMessage) => (
              <div key={msg.id} className={`flex flex-col ${msg.senderId === 'me' ? 'items-end' : 'items-start'}`}>
                
                <div className={`max-w-[85%] lg:max-w-[70%] p-4 lg:p-6 lg:rounded-none rounded-2xl ${
                  msg.senderId === 'me' 
                  ? 'bg-white text-black rounded-tr-none lg:border lg:border-white' 
                  : 'bg-zinc-950 border border-zinc-800 text-white rounded-tl-none'
                }`}>
                  <p className="text-sm lg:text-base font-mono leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                  
                  {/* Show suggested tags if available */}
                  {msg.suggestedTags && msg.suggestedTags.length > 0 && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {msg.suggestedTags.map(tag => (
                        <span 
                          key={tag} 
                          className="text-[10px] px-2 py-1 bg-zinc-800 text-zinc-400 rounded-full font-mono cursor-pointer hover:bg-zinc-700 transition-colors"
                          onClick={() => {
                            setInput(`I'm looking for ${tag} style pieces`);
                            handleSend(`I'm looking for ${tag} style pieces`);
                          }}
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Product Recommendations */}
                {msg.products && msg.products.length > 0 && (
                  <div className="w-full mt-4">
                    <p className="text-xs text-zinc-500 font-mono mb-3 uppercase tracking-wider">✨ RECOMMENDED FOR YOU</p>
                    <div className="flex gap-4 overflow-x-auto w-full pb-4 scrollbar-hide snap-x">
                      {msg.products.map((p: Product) => (
                        <div 
                          key={p.id} 
                          onClick={() => onSelectProduct(p)} 
                          className="snap-start flex-shrink-0 w-40 lg:w-48 bg-black border border-zinc-800 lg:rounded-none rounded-2xl overflow-hidden hover:border-white transition-all cursor-pointer group hover:scale-105 duration-300"
                        >
                          <div className="aspect-[4/5] bg-zinc-900 relative overflow-hidden">
                            <img 
                              src={p.image} 
                              className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500" 
                              alt={p.name} 
                            />
                            <div className="absolute top-3 right-3 bg-black/90 backdrop-blur text-white px-2 py-1 rounded text-[10px] font-black font-mono border border-zinc-700">
                              R {p.price.toLocaleString()}
                            </div>
                          </div>
                          <div className="p-4 bg-zinc-950">
                            <p className="text-xs font-black text-white uppercase truncate mb-1">{p.name}</p>
                            <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest">{p.brand}</p>
                            <p className="text-[10px] text-zinc-600 mt-2 font-mono uppercase">{p.category}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button 
                      onClick={() => {
                        const newMsg = `I love these recommendations! Can you show me more like the ${msg.products?.[0]?.name}?`;
                        setInput(newMsg);
                        handleSend(newMsg);
                      }}
                      className="mt-2 text-xs text-zinc-500 hover:text-white font-mono uppercase tracking-wider transition-colors"
                    >
                      Show more like these →
                    </button>
                  </div>
                )}
                
                <span className="text-[10px] text-zinc-600 mt-2 font-mono uppercase tracking-widest px-1">
                  {msg.createdAt}
                </span>
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-zinc-950 border border-zinc-800 p-6 lg:rounded-none rounded-2xl rounded-tl-none flex gap-3 items-center">
                <Loader2 size={16} className="text-white animate-spin" />
                <span className="text-zinc-500 text-xs font-mono uppercase tracking-widest">Finding your perfect pieces...</span>
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>
        
        <div className="p-4 lg:p-8 border-t border-zinc-800 bg-black z-20">
          <div className="flex items-center gap-4 bg-zinc-950 lg:rounded-none rounded-full p-2 lg:p-3 border border-zinc-800 focus-within:border-white transition-colors max-w-4xl mx-auto">
            <input 
              type="text" 
              value={input} 
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Describe your style, budget, or what you're looking for..."
              className="flex-1 bg-transparent text-white font-mono text-sm placeholder:text-zinc-600 outline-none h-12 px-6"
              disabled={isLoading}
            />
            <button 
              onClick={() => handleSend()} 
              disabled={isLoading || !input.trim()} 
              className="w-12 h-12 lg:w-14 lg:h-14 lg:rounded-none rounded-full bg-white flex items-center justify-center text-black disabled:opacity-30 hover:bg-zinc-200 transition-colors shrink-0"
            >
              <Send size={20} className="ml-1" />
            </button>
          </div>
          <div className="text-center mt-3">
            <p className="text-[10px] text-zinc-600 font-mono">
              💡 Try: "Show me Y2K style under R1000" or "Match this with my cart"
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};